import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add, Delete, PhotoCamera, ReceiptLong, Edit, Close, FilterList, Clear, ZoomIn, ZoomOut, Download, Add as AddIcon, NavigateBefore, NavigateNext, Collections, CameraAlt, Refresh, AccountBalance } from '@mui/icons-material';
import { collectionsAPI, Collection, CollectionSummary, marketRatesAPI, MarketRatesToday } from '../services/api';
import { compressImage } from '../services/imageCompression';

const materialOptions = ['Gold', 'Silver', 'Platinum', 'Diamond'] as const;

const CollectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [material, setMaterial] = useState<typeof materialOptions[number]>('Gold');
  const [categories, setCategories] = useState<string[]>([]);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [summary, setSummary] = useState<CollectionSummary | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewImageAlt, setPreviewImageAlt] = useState<string>('');
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [bankDepositDraft, setBankDepositDraft] = useState({ deposited_on: '', received_per_gram_rate: '' });
  const [bankDepositReceiptDraft, setBankDepositReceiptDraft] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bookViewOpen, setBookViewOpen] = useState(false);
  const [bookViewCollection, setBookViewCollection] = useState<Collection | null>(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(true);
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [marketRates, setMarketRates] = useState<MarketRatesToday | null>(null);
  const [loadingRates, setLoadingRates] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    material: '',
    category: '',
    minWeight: '',
    maxWeight: '',
    minPurchaseAmount: '',
    maxPurchaseAmount: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    material: 'Gold' as typeof materialOptions[number],
    weight: '',
    category: '',
    customCategory: '',
    purchaseAmount: '',
    perGramRate: '',
    inBank: false,
    mainImageFile: null as File | null,
    billImageFile: null as File | null,
  });

  const activeCategory = useMemo(() => {
    return form.category === 'Other' ? form.customCategory : form.category;
  }, [form.category, form.customCategory]);

  // Combine API-provided categories with categories present in summary weight_by_category
  const allCategories = useMemo(() => {
    const combined = new Set<string>();
    (categories || []).forEach((c) => c && combined.add(c));
    const summaryCategories = summary && (summary as any).weight_by_category ? Object.keys((summary as any).weight_by_category) : [];
    (summaryCategories || []).forEach((c) => c && combined.add(c));
    return Array.from(combined).sort((a, b) => a.localeCompare(b));
  }, [categories, summary]);

  // Calculate market value by material
  const marketValueByMaterial = useMemo(() => {
    if (!summary || !marketRates) return {};
    
    const values: Record<string, number> = {};
    const weightByMaterial = summary.weight_by_material || {};
    
    Object.entries(weightByMaterial).forEach(([material, weightStr]) => {
      const weightValue = parseFloat(weightStr.toString().replace(' grams', ''));
      if (weightValue > 0) {
        let rate: number | null = null;
        
        if (material === 'Gold' && marketRates.gold_22k_per_gram) {
          rate = marketRates.gold_22k_per_gram;
        } else if (material === 'Silver' && marketRates.silver_per_gram) {
          rate = marketRates.silver_per_gram;
        } else if (material === 'Platinum' && marketRates.platinum_per_gram) {
          rate = marketRates.platinum_per_gram;
        }
        
        if (rate && rate > 0) {
          values[material] = weightValue * rate;
        }
      }
    });
    
    return values;
  }, [summary, marketRates]);

  // Calculate total market value
  const totalMarketValue = useMemo(() => {
    return Object.values(marketValueByMaterial).reduce((sum, value) => sum + value, 0);
  }, [marketValueByMaterial]);

  // Format currency display
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
  };

  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build filter parameters
      const params: any = {};
      if (filters.material) params.material = filters.material;
      if (filters.category) params.category = filters.category;
      
      const res = await collectionsAPI.getAll(params);
      const data = res.data?.data;
      
      // Apply client-side filters for weight and purchase amount
      let filteredData = Array.isArray(data) ? data : [];
      
      if (filters.minWeight) {
        filteredData = filteredData.filter(c => c.weight >= parseFloat(filters.minWeight));
      }
      if (filters.maxWeight) {
        filteredData = filteredData.filter(c => c.weight <= parseFloat(filters.maxWeight));
      }
      if (filters.minPurchaseAmount) {
        filteredData = filteredData.filter(c => c.purchase_amount && c.purchase_amount >= parseFloat(filters.minPurchaseAmount));
      }
      if (filters.maxPurchaseAmount) {
        filteredData = filteredData.filter(c => c.purchase_amount && c.purchase_amount <= parseFloat(filters.maxPurchaseAmount));
      }
      
      setCollections(filteredData);
    } catch (e: any) {
      console.error('Error fetching collections:', e);
      setError(e.response?.data?.error || 'Failed to load collections');
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchCategories = async (mat: typeof materialOptions[number]) => {
    try {
      const res = await collectionsAPI.getCategories(mat);
      const data = res.data?.data || {};
      const categoriesData = data.categories;
      
      // Ensure categories is always an array
      if (Array.isArray(categoriesData)) {
        setCategories(categoriesData);
      } else {
        console.warn('Categories data is not an array:', categoriesData);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchSummary = useCallback(async () => {
    try {
      const res = await collectionsAPI.getSummary();
      const data = res.data?.data;
      if (data) setSummary(data);
    } catch (e) {
      console.error('Failed to fetch summary:', e);
    }
  }, []);

  const fetchMarketRates = useCallback(async () => {
    try {
      setLoadingRates(true);
      const res = await marketRatesAPI.getToday();
      const data = res.data?.data;
      if (data) setMarketRates(data);
    } catch (e: any) {
      console.error('Failed to fetch market rates:', e);
      setError(e.response?.data?.message || 'Failed to fetch market rates');
    } finally {
      setLoadingRates(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch data after OTP verification
    if (otpVerified) {
      fetchCollections();
      fetchSummary();
    }
  }, [otpVerified, fetchCollections, fetchSummary]);

  useEffect(() => {
    fetchCategories(material);
  }, [material]);

  useEffect(() => {
    // Only fetch collections when filters change if OTP is verified
    if (otpVerified) {
      fetchCollections();
    }
  }, [otpVerified, fetchCollections]);

  useEffect(() => {
    if (imageUploadOpen && selectedCollection?.in_bank) {
      setBankDepositDraft({
        deposited_on: new Date().toISOString().slice(0, 10),
        received_per_gram_rate: '',
      });
      setBankDepositReceiptDraft(null);
    }
  }, [imageUploadOpen, selectedCollection?.id, selectedCollection?.in_bank]);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      material: 'Gold',
      weight: '',
      category: '',
      customCategory: '',
      purchaseAmount: '',
      perGramRate: '',
      inBank: false,
      mainImageFile: null,
      billImageFile: null,
    });
    setMaterial('Gold');
  };

  const resetFilters = () => {
    setFilters({
      material: '',
      category: '',
      minWeight: '',
      maxWeight: '',
      minPurchaseAmount: '',
      maxPurchaseAmount: '',
    });
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        material: form.material,
        weight: parseFloat(form.weight),
        category: (activeCategory || '').trim(),
        purchase_amount: form.purchaseAmount ? parseFloat(form.purchaseAmount) : undefined,
        purchase_per_gram_rate: form.perGramRate ? parseFloat(form.perGramRate) : undefined,
        in_bank: form.inBank,
      };
      if (!payload.name || !payload.material || !payload.category || !payload.weight || isNaN(payload.weight)) {
        setError('Please fill name, material, category and valid weight');
        setSaving(false);
        return;
      }
      const createRes = await collectionsAPI.create(payload);
      const created: Collection = createRes.data?.data;
      if (created?.id) {
        if (form.mainImageFile) {
          await collectionsAPI.uploadImage(created.id, form.mainImageFile, 'front');
        }
        if (form.billImageFile) {
          await collectionsAPI.uploadImage(created.id, form.billImageFile, 'bill');
        }
      }
      await fetchCollections();
      await fetchSummary();
      resetForm();
    } catch (e: any) {
      setError(e.response?.data?.errors?.join(', ') || e.response?.data?.error || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (collection: Collection) => {
    setEditingCollection(collection);
    setForm({
      name: collection.name,
      description: collection.description || '',
      material: collection.material,
      weight: collection.weight.toString(),
      category: collection.category,
      customCategory: '',
      purchaseAmount: collection.purchase_amount?.toString() || '',
      perGramRate: (collection as any).purchase_per_gram_rate?.toString() || (collection as any).per_gram_rate?.toString() || '',
      inBank: Boolean(collection.in_bank),
      mainImageFile: null,
      billImageFile: null,
    });
    setMaterial(collection.material);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingCollection) return;
    
    try {
      setSaving(true);
      setError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        material: form.material,
        weight: parseFloat(form.weight),
        category: (activeCategory || '').trim(),
        purchase_amount: form.purchaseAmount ? parseFloat(form.purchaseAmount) : undefined,
        purchase_per_gram_rate: form.perGramRate ? parseFloat(form.perGramRate) : undefined,
        in_bank: form.inBank,
      };
      
      if (!payload.name || !payload.material || !payload.category || !payload.weight || isNaN(payload.weight)) {
        setError('Please fill name, material, category and valid weight');
        setSaving(false);
        return;
      }
      
      await collectionsAPI.update(editingCollection.id, payload);
      
      if (form.mainImageFile) {
        await collectionsAPI.uploadImage(editingCollection.id, form.mainImageFile, 'front');
      }
      if (form.billImageFile) {
        await collectionsAPI.uploadImage(editingCollection.id, form.billImageFile, 'bill');
      }
      
      await fetchCollections();
      await fetchSummary();
      setEditDialogOpen(false);
      setEditingCollection(null);
      resetForm();
    } catch (e: any) {
      setError(e.response?.data?.errors?.join(', ') || e.response?.data?.error || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    console.log('Delete button clicked for collection ID:', id);
    if (!window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) return;
    
    try {
      console.log('Attempting to delete collection:', id);
      await collectionsAPI.delete(id);
      console.log('Collection deleted successfully');
      await fetchCollections();
      await fetchSummary();
    } catch (e: any) {
      console.error('Error deleting collection:', e);
      setError(e.response?.data?.error || 'Failed to delete collection');
    }
  };

  const handleImageClick = (collection: Collection, imageType: 'collection' | 'bill' = 'collection') => {
    let images: string[] = [];
    let imageLabels: string[] = [];
    
    if (imageType === 'collection') {
      if (collection.front_image_url) { images.push(collection.front_image_url); imageLabels.push('Front View'); }
      if (collection.back_image_url) { images.push(collection.back_image_url); imageLabels.push('Back View'); }
      if (collection.top_image_url) { images.push(collection.top_image_url); imageLabels.push('Top View'); }
      if (collection.bottom_image_url) { images.push(collection.bottom_image_url); imageLabels.push('Bottom View'); }
    } else {
      // Include all bill images
      if (collection.bill_images && collection.bill_images.length > 0) {
        collection.bill_images.forEach((billImage, index) => {
          images.push(billImage.url);
          imageLabels.push(`Bill ${index + 1}${billImage.filename ? ` - ${billImage.filename}` : ''}`);
        });
      }
      // Fallback to legacy bill_image_url if no bill_images
      if (images.length === 0 && collection.bill_image_url) {
        images.push(collection.bill_image_url);
        imageLabels.push('Bill');
      }
    }
    
    if (images.length > 0) {
      setGalleryImages(images);
      setCurrentImageIndex(0);
      setPreviewImage(images[0]);
      setPreviewImageAlt(`${collection.name} - ${imageLabels[0]}`);
      setImagePreviewOpen(true);
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleDownloadImage = () => {
    if (previewImage) {
      const link = document.createElement('a');
      link.href = previewImage;
      link.download = `${previewImageAlt || 'collection-image'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setPreviewImage(galleryImages[newIndex]);
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < galleryImages.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setPreviewImage(galleryImages[newIndex]);
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleImageUpload = async (
    collection: Collection,
    imageType: 'front' | 'back' | 'top' | 'bottom' | 'bill',
    file: File
  ) => {
    console.log('Starting image upload:', {
      collectionId: collection.id,
      imageType,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    setUploadingImage(true);
    try {
      // Compress before upload (keep PDFs as-is for bill uploads)
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const compressed = isPdf ? file : await compressImage(file, {
        maxWidth: 1600,
        maxHeight: 1600,
        quality: 0.8,
        outputType: 'image/jpeg'
      });
      await collectionsAPI.uploadImage(collection.id, compressed, imageType);
      console.log('Image upload successful');
      await fetchCollections(); // Refresh the collections list
      setError(null);
    } catch (e: any) {
      console.error('Image upload failed:', e);
      console.error('Error response:', e.response?.data);
      setError(e.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleMultipleBillUpload = async (collection: Collection, files: File[]) => {
    console.log('Starting multiple bill upload:', {
      collectionId: collection.id,
      fileCount: files.length,
      fileNames: files.map(f => f.name)
    });
    
    setUploadingImage(true);
    try {
      // Compress all images before upload (keep PDFs as-is)
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
          if (isPdf) return file;
          
          try {
            return await compressImage(file, {
              maxWidth: 1600,
              maxHeight: 1600,
              quality: 0.8,
              outputType: 'image/jpeg'
            });
          } catch (compressErr) {
            console.warn('Image compression failed, using original:', compressErr);
            return file;
          }
        })
      );
      
      await collectionsAPI.uploadBillImages(collection.id, compressedFiles);
      console.log('Multiple bill upload successful');
      await fetchCollections(); // Refresh the collections list
      setError(null);
    } catch (e: any) {
      console.error('Multiple bill upload failed:', e);
      console.error('Error response:', e.response?.data);
      setError(e.response?.data?.message || 'Failed to upload bill images');
    } finally {
      setUploadingImage(false);
    }
  };

  const openImageUpload = (collection: Collection) => {
    setSelectedCollection(collection);
    setImageUploadOpen(true);
  };

  const openBookView = (collection: Collection) => {
    setBookViewCollection(collection);
    setBookViewOpen(true);
  };

  const handleDeleteImage = async (
    collectionId: number,
    imageType: 'front' | 'back' | 'top' | 'bottom' | 'bill',
    billImageId?: number
  ) => {
    if (!window.confirm(`Are you sure you want to delete the ${imageType} image? This action cannot be undone.`)) return;
    
    try {
      console.log('Deleting image:', imageType, 'from collection:', collectionId, billImageId ? `bill_image_id: ${billImageId}` : '');
      if (imageType === 'bill' && billImageId) {
        await collectionsAPI.deleteBillImage(collectionId, billImageId);
      } else {
        await collectionsAPI.deleteImage(collectionId, imageType);
      }
      console.log('Image deleted successfully');
      await fetchCollections(); // Refresh the collections list
      setError(null);
    } catch (e: any) {
      console.error('Error deleting image:', e);
      setError(e.response?.data?.error || 'Failed to delete image');
    }
  };

  const mergeCollectionIntoUiState = async (collectionId: number) => {
    try {
      const res = await collectionsAPI.getById(collectionId);
      const fresh: Collection | undefined = res.data?.data;
      if (!fresh) return;
      setCollections((prev) => prev.map((c) => (c.id === collectionId ? fresh : c)));
      setSelectedCollection((prev) => (prev?.id === collectionId ? fresh : prev));
      setBookViewCollection((prev) => (prev?.id === collectionId ? fresh : prev));
      setEditingCollection((prev) => (prev?.id === collectionId ? fresh : prev));
    } catch (e) {
      console.error(e);
      await fetchCollections();
    }
  };

  const handleCreateBankDeposit = async () => {
    if (!selectedCollection?.in_bank) return;
    const rate = parseFloat(bankDepositDraft.received_per_gram_rate);
    if (!bankDepositDraft.deposited_on || Number.isNaN(rate) || rate <= 0) {
      setError('Enter a valid date and amount received per gram.');
      return;
    }
    setUploadingImage(true);
    setError(null);
    try {
      await collectionsAPI.createBankDeposit(selectedCollection.id, {
        deposited_on: bankDepositDraft.deposited_on,
        received_per_gram_rate: rate,
        receipt: bankDepositReceiptDraft,
      });
      await mergeCollectionIntoUiState(selectedCollection.id);
      setBankDepositDraft({
        deposited_on: new Date().toISOString().slice(0, 10),
        received_per_gram_rate: '',
      });
      setBankDepositReceiptDraft(null);
    } catch (e: any) {
      setError(e.response?.data?.errors?.join(', ') || e.response?.data?.message || 'Failed to add bank deposit');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteBankDeposit = async (depositId: number) => {
    if (!selectedCollection) return;
    if (!window.confirm('Remove this bank deposit record?')) return;
    setUploadingImage(true);
    setError(null);
    try {
      await collectionsAPI.deleteBankDeposit(selectedCollection.id, depositId);
      await mergeCollectionIntoUiState(selectedCollection.id);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete bank deposit');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBankDepositReceiptUpload = async (depositId: number, file: File) => {
    if (!selectedCollection) return;
    setUploadingImage(true);
    setError(null);
    try {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const toSend = isPdf
        ? file
        : await compressImage(file, {
            maxWidth: 1600,
            maxHeight: 1600,
            quality: 0.8,
            outputType: 'image/jpeg',
          });
      await collectionsAPI.uploadBankDepositReceipt(selectedCollection.id, depositId, toSend);
      await mergeCollectionIntoUiState(selectedCollection.id);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to upload receipt');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteBankDepositReceipt = async (depositId: number) => {
    if (!selectedCollection) return;
    if (!window.confirm('Remove receipt from this deposit?')) return;
    setUploadingImage(true);
    setError(null);
    try {
      await collectionsAPI.deleteBankDepositReceipt(selectedCollection.id, depositId);
      await mergeCollectionIntoUiState(selectedCollection.id);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to remove receipt');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (otpInput === '1105') {
      setOtpVerified(true);
      setOtpDialogOpen(false);
      setOtpError('');
      setLoading(true);
      try {
        // Now fetch the collections data after OTP verification
        await fetchCollections();
        await fetchSummary();
      } catch (e) {
        console.error('Error fetching data after OTP verification:', e);
      } finally {
        setLoading(false);
      }
    } else {
      setOtpError('Invalid OTP. Please try again.');
      setOtpInput('');
    }
  };

  const handleOtpKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleOtpSubmit();
    } else if (e.key === 'Escape') {
      handleOtpCancel();
    }
  };

  const handleOtpCancel = () => {
    setOtpDialogOpen(false);
    setOtpInput('');
    setOtpError('');
    navigate('/'); // Redirect to home page
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getMaterialColor = (material: string) => {
    switch (material) {
      case 'Gold':
        return '#FFD700'; // Gold color
      case 'Silver':
        return '#C0C0C0'; // Silver color
      case 'Platinum':
        return '#E5E4E2'; // Platinum color
      case 'Diamond':
        return '#B9F2FF'; // Diamond blue-white color
      default:
        return '#1976d2'; // Default blue
    }
  };

  return (
    <Box>
      {/* OTP Protection Dialog */}
      <Dialog 
        open={otpDialogOpen} 
        onClose={handleOtpCancel} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                backgroundColor: '#1976d2', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  🔒
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6">
                  Access Collections
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter OTP to view your collections
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={handleOtpCancel}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="Enter OTP"
              type="password"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              onKeyPress={handleOtpKeyPress}
              error={!!otpError}
              helperText={otpError}
              placeholder="Enter 4-digit OTP"
              inputProps={{ 
                maxLength: 4,
                style: { 
                  textAlign: 'center', 
                  fontSize: '1.5rem', 
                  letterSpacing: '0.5rem',
                  fontWeight: 'bold'
                } 
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2,
                  },
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleOtpCancel}
            variant="outlined"
            size="large"
            sx={{ 
              py: 1.5,
              px: 3,
              fontSize: '1rem',
              fontWeight: 'bold',
              minWidth: '120px'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleOtpSubmit}
            variant="contained"
            size="large"
            disabled={otpInput.length !== 4}
            sx={{ 
              py: 1.5,
              px: 3,
              fontSize: '1rem',
              fontWeight: 'bold',
              minWidth: '160px'
            }}
          >
            Access Collections
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content - Only show after OTP verification */}
      {otpVerified && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4">
              Collections
            </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingCollection(null);
            setEditDialogOpen(true);
            resetForm();
          }}
          sx={{ borderRadius: '50px' }}
        >
          Add Collection
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Summary Section */}
      {summary && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Collection Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Weight by Material
              </Typography>
              {Object.entries(summary.weight_by_material).map(([material, weight]) => {
                // Extract numeric value from weight string (e.g., "28.0 grams" -> 28.0)
                const weightValue = parseFloat(weight.toString().replace(' grams', ''));
                const displayWeight = material === 'Gold' && weightValue > 0
                  ? `${weightValue} grams (${(weightValue / 8.0).toFixed(2)} pavan)`
                  : weight;
                
                return (
                  <Box key={material} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">{material}:</Typography>
                    <Typography variant="body2" fontWeight="bold">{displayWeight}</Typography>
                  </Box>
                );
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Weight by Category
              </Typography>
              {Object.entries(summary.weight_by_category).map(([category, weight]) => {
                // Extract numeric value from weight string (e.g., "28.0 grams" -> 28.0)
                const weightValue = parseFloat(weight.toString().replace(' grams', ''));
                // For categories, we need to check if this category contains gold items
                // We'll show pavan for all categories since they might contain gold
                const displayWeight = weightValue > 0
                  ? `${weightValue} grams`
                  : weight;
                
                return (
                  <Box key={category} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2">{category}:</Typography>
                    <Typography variant="body2" fontWeight="bold">{displayWeight}</Typography>
                  </Box>
                );
              })}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Total Purchase Amount:</Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    {summary.total_purchase_amount_display}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Total Market Value:</Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'success.main' }}>
                    {marketRates && totalMarketValue > 0 ? formatCurrency(totalMarketValue) : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Market Rates & Value by Material:
                  </Typography>
                  <IconButton
                    onClick={fetchMarketRates}
                    disabled={loadingRates}
                    color="primary"
                    size="small"
                    title="Refresh Market Rates"
                    sx={{
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        transform: loadingRates ? 'none' : 'rotate(360deg)',
                        transition: 'transform 0.5s'
                      }
                    }}
                  >
                    <Refresh sx={{ 
                      animation: loadingRates ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }} />
                  </IconButton>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {marketRates ? (
                    <>
                      {marketRates.gold_22k_per_gram && marketRates.gold_22k_per_gram > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ 
                            color: getMaterialColor('Gold'),
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            Gold 22K:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {marketRates.gold_22k_per_gram_display}/gram
                          </Typography>
                          {marketValueByMaterial['Gold'] && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                {formatCurrency(marketValueByMaterial['Gold'])}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                      {marketRates.silver_per_gram && marketRates.silver_per_gram > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ 
                            color: getMaterialColor('Silver'),
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            Silver:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {marketRates.silver_per_gram_display}/gram
                          </Typography>
                          {marketValueByMaterial['Silver'] && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                {formatCurrency(marketValueByMaterial['Silver'])}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                      {marketRates.platinum_per_gram && marketRates.platinum_per_gram > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ 
                            color: getMaterialColor('Platinum'),
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            Platinum:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {marketRates.platinum_per_gram_display}/gram
                          </Typography>
                          {marketValueByMaterial['Platinum'] && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                {formatCurrency(marketValueByMaterial['Platinum'])}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Click refresh to load market rates
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Items
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={resetFilters}
              size="small"
              disabled={!Object.values(filters).some(v => v !== '')}
            >
              Clear
            </Button>
          </Box>
        </Box>

        {/* Filter Controls */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Material"
                  value={filters.material}
                  onChange={(e) => handleFilterChange('material', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All Materials</MenuItem>
                  {materialOptions.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {allCategories.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Min Weight (grams)"
                  type="number"
                  value={filters.minWeight}
                  onChange={(e) => handleFilterChange('minWeight', e.target.value)}
                  size="small"
                  inputProps={{ step: '0.001' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Max Weight (grams)"
                  type="number"
                  value={filters.maxWeight}
                  onChange={(e) => handleFilterChange('maxWeight', e.target.value)}
                  size="small"
                  inputProps={{ step: '0.001' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Min Purchase Amount (₹)"
                  type="number"
                  value={filters.minPurchaseAmount}
                  onChange={(e) => handleFilterChange('minPurchaseAmount', e.target.value)}
                  size="small"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Max Purchase Amount (₹)"
                  type="number"
                  value={filters.maxPurchaseAmount}
                  onChange={(e) => handleFilterChange('maxPurchaseAmount', e.target.value)}
                  size="small"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Active Filters Summary */}
        {Object.values(filters).some(v => v !== '') && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Active Filters:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {filters.material && (
                <Chip
                  label={`Material: ${filters.material}`}
                  size="small"
                  onDelete={() => handleFilterChange('material', '')}
                />
              )}
              {filters.category && (
                <Chip
                  label={`Category: ${filters.category}`}
                  size="small"
                  onDelete={() => handleFilterChange('category', '')}
                />
              )}
              {filters.minWeight && (
                <Chip
                  label={`Min Weight: ${filters.minWeight}g`}
                  size="small"
                  onDelete={() => handleFilterChange('minWeight', '')}
                />
              )}
              {filters.maxWeight && (
                <Chip
                  label={`Max Weight: ${filters.maxWeight}g`}
                  size="small"
                  onDelete={() => handleFilterChange('maxWeight', '')}
                />
              )}
              {filters.minPurchaseAmount && (
                <Chip
                  label={`Min Amount: ₹${filters.minPurchaseAmount}`}
                  size="small"
                  onDelete={() => handleFilterChange('minPurchaseAmount', '')}
                />
              )}
              {filters.maxPurchaseAmount && (
                <Chip
                  label={`Max Amount: ₹${filters.maxPurchaseAmount}`}
                  size="small"
                  onDelete={() => handleFilterChange('maxPurchaseAmount', '')}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {Array.isArray(collections) && collections.map((c) => {
            // Debug logging
            console.log('Collection data:', {
              id: c.id,
              name: c.name,
              has_image: c.has_image,
              image_count: c.image_count,
              front_image: c.has_front_image,
              back_image: c.has_back_image,
              top_image: c.has_top_image,
              bottom_image: c.has_bottom_image,
              bill_image: c.has_bill_image,
              front_image_url: c.front_image_url,
              back_image_url: c.back_image_url,
              top_image_url: c.top_image_url,
              bottom_image_url: c.bottom_image_url,
              bill_image_url: c.bill_image_url
            });
            
            return (
            <Grid item xs={12} md={6} lg={4} key={c.id}>
              <Card>
                {c.has_image && (c.front_image_url || c.back_image_url || c.top_image_url || c.bottom_image_url || c.image_url || c.image_data || c.image) ? (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={c.front_image_url || c.back_image_url || c.top_image_url || c.bottom_image_url || c.image_url || c.image_data || `https://aparmathi.com/${c.image}`}
                      alt={c.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImageClick(c, 'collection')}
                      onLoad={() => {
                        console.log('Image loaded successfully for collection:', c.name);
                      }}
                      onError={(e) => {
                        console.error('Image failed to load for collection:', c.name, {
                          image_url: c.image_url,
                          image_data: c.image_data ? 'present' : 'missing',
                          image: c.image,
                          has_image: c.has_image
                        });
                        // Fallback to placeholder if image fails to load
                        e.currentTarget.style.display = 'none';
                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                        if (nextElement) {
                          nextElement.style.display = 'flex';
                        }
                      }}
                    />
                    {/* Gallery indicator */}
                    {c.image_count > 1 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        <Collections sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                    
                    {/* Image count badge */}
                    {c.image_count > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {c.image_count} views
                      </Box>
                    )}
                    
                    <Box
                      sx={{
                        display: 'none',
                        height: '100%',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        flexDirection: 'column'
                      }}
                    >
                      <PhotoCamera sx={{ fontSize: 48, color: '#666', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Image not found
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Box textAlign="center">
                      <PhotoCamera sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No image
                      </Typography>
                    </Box>
                  </Box>
                )}
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{c.name}</Typography>
                    <Chip 
                      label={c.material} 
                      size="small" 
                      variant="outlined"
                      sx={{
                        borderColor: getMaterialColor(c.material),
                        color: getMaterialColor(c.material),
                        '&:hover': {
                          backgroundColor: getMaterialColor(c.material),
                          color: 'white'
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {c.description || '—'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Weight: {c.weight_display}</Typography>
                    <Typography variant="body2">Category: {c.category}</Typography>
                    <Typography variant="body2">Purchase: {c.purchase_amount_display}</Typography>
                    {c.per_gram_rate_display && c.per_gram_rate_display !== 'Not specified' && (
                      <Typography variant="body2" color="primary">
                        Rate: {c.per_gram_rate_display}
                      </Typography>
                    )}
                    {c.calculated_per_gram_rate_display && c.calculated_per_gram_rate_display !== 'Not calculable' && (
                      <Typography variant="body2" color="text.secondary">
                        Calculated: {c.calculated_per_gram_rate_display}
                      </Typography>
                    )}
                    {c.in_bank && c.latest_bank_received_display && (
                      <Typography variant="body2" color="secondary" sx={{ mt: 0.5 }}>
                        In bank — latest: {c.latest_bank_received_display}
                        {c.latest_bank_deposited_on ? ` (${c.latest_bank_deposited_on})` : ''}
                      </Typography>
                    )}
                    {c.in_bank && !c.has_bank_deposits && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Add bank deposits from Upload images.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {c.has_image && <Chip label="Image" size="small" color="success" />}
                    {c.has_bill && <Chip label="Bill" size="small" color="info" />}
                    {c.in_bank && <Chip label="In bank" size="small" color="secondary" variant="outlined" />}
                    {c.has_bank_deposits && (
                      <Chip
                        label={`Bank deposits (${c.bank_deposits_count ?? c.bank_deposits?.length ?? 0})`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, minHeight: '48px' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <IconButton 
                      color="primary" 
                      onClick={() => openImageUpload(c)}
                      size="small"
                      title="Upload Images"
                    >
                      <CameraAlt />
                    </IconButton>
                    {c.image_count > 0 && (
                      <IconButton 
                        color="secondary" 
                        onClick={() => openBookView(c)}
                        size="small"
                        title="View All Images"
                      >
                        <Collections />
                      </IconButton>
                    )}
                    <IconButton color="primary" onClick={() => handleEdit(c)} title="Edit Collection">
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(c.id)} title="Delete Collection">
                      <Delete />
                    </IconButton>
                  </Box>
                  
                  {/* Image Status Indicators */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {c.has_front_image && (
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#4caf50',
                        title: 'Front View'
                      }} />
                    )}
                    {c.has_back_image && (
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#2196f3',
                        title: 'Back View'
                      }} />
                    )}
                    {c.has_top_image && (
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#ff9800',
                        title: 'Top View'
                      }} />
                    )}
                    {c.has_bottom_image && (
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#9c27b0',
                        title: 'Bottom View'
                      }} />
                    )}
                    {c.has_bill_image && (
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#f44336',
                        title: 'Bill'
                      }} />
                    )}
                    {c.in_bank && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#00897b',
                          title: 'In bank',
                        }}
                      />
                    )}
                    {c.has_bank_deposits && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#00695c',
                          title: 'Bank deposits',
                        }}
                      />
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCollection ? `Edit Collection - ${editingCollection.name}` : 'Add New Collection'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Material"
                value={form.material}
                onChange={(e) => {
                  const val = e.target.value as typeof materialOptions[number];
                  setForm({ ...form, material: val, category: '', customCategory: '' });
                  setMaterial(val);
                }}
              >
                {materialOptions.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: '0.001' }}
                label="Weight (grams)"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: '0.01' }}
                label="Purchase Amount (₹) - Optional"
                value={form.purchaseAmount}
                onChange={(e) => setForm({ ...form, purchaseAmount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: '0.01' }}
                label="Purchase Per Gram Rate (₹) - Optional"
                value={form.perGramRate}
                onChange={(e) => setForm({ ...form, perGramRate: e.target.value })}
                helperText="Rate per gram for this item"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.inBank}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setForm({
                        ...form,
                        inBank: v,
                      });
                    }}
                  />
                }
                label="Item is deposited in bank (locker / pledge / bank custody)"
              />
            </Grid>
            {form.inBank && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Record each bank credit under <strong>Upload images</strong> (camera icon on the card): deposit date,
                  amount received per gram, and receipt. You can add multiple entries over time.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Array.isArray(allCategories) && allCategories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
                <MenuItem value="Other">Other (free text)</MenuItem>
              </TextField>
            </Grid>
            {form.category === 'Other' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Custom Category"
                  value={form.customCategory}
                  onChange={(e) => setForm({ ...form, customCategory: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{ mr: 2 }}
              >
                Update Image
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      // Validate file type - enhanced for iOS and Android compatibility
                      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
                      
                      // iOS sometimes sends empty or incorrect MIME types
                      let fileType = file.type;
                      if (!fileType || fileType === '') {
                        // Try to determine type from filename for iOS/mobile
                        const fileName = file.name.toLowerCase();
                        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
                          fileType = 'image/jpeg';
                        } else if (fileName.endsWith('.png')) {
                          fileType = 'image/png';
                        } else if (fileName.endsWith('.gif')) {
                          fileType = 'image/gif';
                        } else if (fileName.endsWith('.webp')) {
                          fileType = 'image/webp';
                        } else if (fileName.endsWith('.heic')) {
                          fileType = 'image/heic';
                        } else if (fileName.endsWith('.heif')) {
                          fileType = 'image/heif';
                        }
                      }
                      
                      // More lenient validation for mobile - check file extension as fallback
                      const fileExtension = file.name.toLowerCase().split('.').pop();
                      const isValidExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(fileExtension || '');
                      const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
                      
                      if (!allowedTypes.includes(fileType) && !isValidExtension && !isMobile) {
                        console.warn('File validation failed:', { 
                          fileName: file.name, 
                          fileType: file.type, 
                          fileSize: file.size,
                          userAgent: navigator.userAgent
                        });
                        setError('Please select a valid image file (JPG, JPEG, PNG, GIF, WebP, HEIC, or HEIF)');
                        return;
                      }
                      
                      // For mobile devices, be more lenient - if it's an image-like file, allow it
                      if (isMobile && !fileType && !isValidExtension) {
                        console.warn('Unknown file type on mobile, allowing:', file.name);
                      }
                      
                      // Validate file size (10MB max)
                      if (file.size > 10 * 1024 * 1024) {
                        setError('File size too large. Maximum size allowed is 10MB');
                        return;
                      }
                      
                      // Validate file is not empty
                      if (file.size === 0) {
                        setError('Selected file is empty. Please choose a valid image.');
                        return;
                      }
                      
                      setForm({ ...form, mainImageFile: file });
                    } else {
                      setForm({ ...form, mainImageFile: null });
                    }
                  }}
                />
              </Button>
              {form.mainImageFile && <Chip label={form.mainImageFile.name} size="small" />}
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ReceiptLong />}
                sx={{ mr: 2 }}
              >
                Update Bill Image
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      // Validate file type - enhanced for iOS and Android compatibility
                      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
                      
                      // iOS sometimes sends empty or incorrect MIME types
                      let fileType = file.type;
                      if (!fileType || fileType === '') {
                        // Try to determine type from filename for iOS/mobile
                        const fileName = file.name.toLowerCase();
                        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
                          fileType = 'image/jpeg';
                        } else if (fileName.endsWith('.png')) {
                          fileType = 'image/png';
                        } else if (fileName.endsWith('.gif')) {
                          fileType = 'image/gif';
                        } else if (fileName.endsWith('.webp')) {
                          fileType = 'image/webp';
                        } else if (fileName.endsWith('.heic')) {
                          fileType = 'image/heic';
                        } else if (fileName.endsWith('.heif')) {
                          fileType = 'image/heif';
                        } else if (fileName.endsWith('.pdf')) {
                          fileType = 'application/pdf';
                        }
                      }
                      
                      // More lenient validation for mobile - check file extension as fallback
                      const fileExtension = file.name.toLowerCase().split('.').pop();
                      const isValidExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'pdf'].includes(fileExtension || '');
                      const isMobile = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
                      
                      if (!allowedTypes.includes(fileType) && !isValidExtension && !isMobile) {
                        console.warn('File validation failed:', { 
                          fileName: file.name, 
                          fileType: file.type, 
                          fileSize: file.size,
                          userAgent: navigator.userAgent
                        });
                        setError('Please select a valid image file or PDF (JPG, JPEG, PNG, GIF, WebP, HEIC, HEIF, or PDF)');
                        return;
                      }
                      
                      // For mobile devices, be more lenient - if it's an image-like file, allow it
                      if (isMobile && !fileType && !isValidExtension) {
                        console.warn('Unknown file type on mobile, allowing:', file.name);
                      }
                      
                      // Validate file size (10MB max)
                      if (file.size > 10 * 1024 * 1024) {
                        setError('File size too large. Maximum size allowed is 10MB');
                        return;
                      }
                      
                      // Validate file is not empty
                      if (file.size === 0) {
                        setError('Selected file is empty. Please choose a valid image or PDF.');
                        return;
                      }
                      
                      setForm({ ...form, billImageFile: file });
                    } else {
                      setForm({ ...form, billImageFile: null });
                    }
                  }}
                />
              </Button>
              {form.billImageFile && <Chip label={form.billImageFile.name} size="small" />}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingCollection ? handleUpdate : handleCreate} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? (editingCollection ? 'Updating...' : 'Creating...') : (editingCollection ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Image Preview Modal */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.9)',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            margin: 0,
            borderRadius: 0
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          {/* Gallery Counter */}
          {galleryImages.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                borderRadius: 2,
                px: 2,
                py: 1,
                zIndex: 1000,
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {currentImageIndex + 1} / {galleryImages.length}
            </Box>
          )}
          {/* Gallery Navigation - Previous Button */}
          {galleryImages.length > 1 && currentImageIndex > 0 && (
            <IconButton
              onClick={handlePreviousImage}
              sx={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <NavigateBefore />
            </IconButton>
          )}

          {/* Gallery Navigation - Next Button */}
          {galleryImages.length > 1 && currentImageIndex < galleryImages.length - 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                zIndex: 1000,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <NavigateNext />
            </IconButton>
          )}

          {/* Image Container */}
          <Box
            sx={{
              position: 'relative',
              cursor: imageZoom > 1 ? 'grab' : 'default',
              '&:active': {
                cursor: imageZoom > 1 ? 'grabbing' : 'default'
              }
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={previewImage}
              alt={previewImageAlt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                userSelect: 'none'
              }}
            />
          </Box>

          {/* Control Buttons */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              gap: 1,
              flexDirection: 'column'
            }}
          >
            {/* Zoom Controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <IconButton
                onClick={handleZoomIn}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                title="Zoom In"
              >
                <ZoomIn />
              </IconButton>
              <IconButton
                onClick={handleZoomOut}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                title="Zoom Out"
              >
                <ZoomOut />
              </IconButton>
              <IconButton
                onClick={handleResetZoom}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                title="Reset Zoom"
              >
                <AddIcon sx={{ transform: 'rotate(45deg)' }} />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <IconButton
                onClick={handleDownloadImage}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                title="Download Image"
              >
                <Download />
              </IconButton>
              <IconButton
                onClick={() => setImagePreviewOpen(false)}
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                title="Close"
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* Zoom Level Indicator */}
          {imageZoom !== 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {Math.round(imageZoom * 100)}%
            </Box>
          )}

          {/* Instructions */}
          {imageZoom > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                maxWidth: '200px'
              }}
            >
              Drag to pan around the image
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog 
        open={imageUploadOpen} 
        onClose={() => setImageUploadOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Upload Images - {selectedCollection?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Image Type and Upload
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Front Image */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: selectedCollection?.has_front_image ? '2px solid #4caf50' : '2px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <PhotoCamera sx={{ fontSize: 48, color: '#4caf50' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Front View
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Main front view of the item
                  </Typography>
                  {selectedCollection?.has_front_image ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip label="Uploaded" color="success" size="small" />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteImage(selectedCollection!.id, 'front')}
                        startIcon={<Delete />}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingImage}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedCollection) {
                            console.log('Front image file selected:', {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              lastModified: file.lastModified
                            });
                            await handleImageUpload(selectedCollection, 'front', file);
                          }
                        }}
                      />
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Back Image */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: selectedCollection?.has_back_image ? '2px solid #2196f3' : '2px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <PhotoCamera sx={{ fontSize: 48, color: '#2196f3' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Back View
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Back side of the item
                  </Typography>
                  {selectedCollection?.has_back_image ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip label="Uploaded" color="success" size="small" />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteImage(selectedCollection!.id, 'back')}
                        startIcon={<Delete />}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingImage}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedCollection) {
                            console.log('Back image file selected:', {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              lastModified: file.lastModified
                            });
                            await handleImageUpload(selectedCollection, 'back', file);
                          }
                        }}
                      />
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Top Image */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: selectedCollection?.has_top_image ? '2px solid #ff9800' : '2px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <PhotoCamera sx={{ fontSize: 48, color: '#ff9800' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Top View
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Top view of the item
                  </Typography>
                  {selectedCollection?.has_top_image ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip label="Uploaded" color="success" size="small" />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteImage(selectedCollection!.id, 'top')}
                        startIcon={<Delete />}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingImage}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedCollection) {
                            console.log('Top image file selected:', {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              lastModified: file.lastModified
                            });
                            await handleImageUpload(selectedCollection, 'top', file);
                          }
                        }}
                      />
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Bottom Image */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: selectedCollection?.has_bottom_image ? '2px solid #9c27b0' : '2px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <PhotoCamera sx={{ fontSize: 48, color: '#9c27b0' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Bottom View
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bottom view of the item
                  </Typography>
                  {selectedCollection?.has_bottom_image ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Chip label="Uploaded" color="success" size="small" />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteImage(selectedCollection!.id, 'bottom')}
                        startIcon={<Delete />}
                      >
                        Delete
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingImage}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && selectedCollection) {
                            console.log('Bottom image file selected:', {
                              name: file.name,
                              size: file.size,
                              type: file.type,
                              lastModified: file.lastModified
                            });
                            await handleImageUpload(selectedCollection, 'bottom', file);
                          }
                        }}
                      />
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Bill Images */}
              <Grid item xs={12} sm={6} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    border: selectedCollection?.has_bill_image ? '2px solid #f44336' : '2px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <ReceiptLong sx={{ fontSize: 48, color: '#f44336' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Bill/Receipt
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Purchase bill or receipt
                    {selectedCollection?.bill_images_count && selectedCollection.bill_images_count > 0 && (
                      <span> ({selectedCollection.bill_images_count} uploaded)</span>
                    )}
                  </Typography>
                  
                  {/* Display existing bill images */}
                  {selectedCollection?.bill_images && selectedCollection.bill_images.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {selectedCollection.bill_images.map((billImage, index) => (
                        <Box 
                          key={billImage.id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 1,
                            p: 1,
                            bgcolor: 'background.default',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="caption" sx={{ flex: 1, textAlign: 'left', ml: 1 }}>
                            {billImage.filename || `Bill ${index + 1}`}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteImage(selectedCollection.id, 'bill', billImage.id)}
                            disabled={uploadingImage}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* Upload button - supports multiple files */}
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    disabled={uploadingImage}
                    startIcon={<ReceiptLong />}
                  >
                    {selectedCollection?.has_bill_image ? 'Add More Bills' : 'Upload Bills'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      capture="environment"
                      multiple
                      onChange={async (e) => {
                        const files = e.target.files ? Array.from(e.target.files) : [];
                        if (files.length > 0 && selectedCollection) {
                          console.log('Bill image files selected:', {
                            count: files.length,
                            fileNames: files.map(f => f.name)
                          });
                          await handleMultipleBillUpload(selectedCollection, files);
                        }
                      }}
                    />
                  </Button>
                  
                  {/* Legacy single bill image support */}
                  {selectedCollection?.bill_image_url && !selectedCollection?.bill_images && (
                    <Box sx={{ mt: 2 }}>
                      <Chip label="Legacy Bill" color="info" size="small" />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => handleDeleteImage(selectedCollection!.id, 'bill')}
                        startIcon={<Delete />}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {selectedCollection?.in_bank && (
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      border: '2px solid #00897b',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AccountBalance sx={{ fontSize: 36, color: '#00897b' }} />
                      <Box>
                        <Typography variant="h6">Bank deposits</Typography>
                        <Typography variant="body2" color="text.secondary">
                          One row per credit: date, amount received per gram, optional receipt (image or PDF).
                        </Typography>
                      </Box>
                    </Box>
                    {(selectedCollection.bank_deposits || []).map((dep) => (
                      <Box
                        key={dep.id}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {dep.deposited_on} — {dep.received_per_gram_rate_display}
                          </Typography>
                          {dep.has_receipt && dep.receipt_url && (
                            <Button size="small" href={dep.receipt_url} target="_blank" rel="noopener noreferrer">
                              Receipt
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <Button variant="outlined" component="label" size="small" disabled={uploadingImage}>
                            {dep.has_receipt ? 'Replace receipt' : 'Attach receipt'}
                            <input
                              type="file"
                              hidden
                              accept="image/*,application/pdf"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) await handleBankDepositReceiptUpload(dep.id, file);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          {dep.has_receipt && (
                            <Button
                              size="small"
                              color="warning"
                              disabled={uploadingImage}
                              onClick={() => handleDeleteBankDepositReceipt(dep.id)}
                            >
                              Remove file
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            disabled={uploadingImage}
                            onClick={() => handleDeleteBankDeposit(dep.id)}
                            title="Delete this deposit row"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Add deposit
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Deposit date"
                          InputLabelProps={{ shrink: true }}
                          value={bankDepositDraft.deposited_on}
                          onChange={(e) => setBankDepositDraft({ ...bankDepositDraft, deposited_on: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Received per gram (₹)"
                          inputProps={{ step: '0.01', min: '0' }}
                          value={bankDepositDraft.received_per_gram_rate}
                          onChange={(e) =>
                            setBankDepositDraft({ ...bankDepositDraft, received_per_gram_rate: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button variant="outlined" component="label" size="small" disabled={uploadingImage}>
                          {bankDepositReceiptDraft ? bankDepositReceiptDraft.name : 'Optional receipt'}
                          <input
                            type="file"
                            hidden
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              setBankDepositReceiptDraft(f || null);
                            }}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={uploadingImage}
                          onClick={() => handleCreateBankDeposit()}
                          startIcon={<AddIcon />}
                        >
                          Save deposit
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {uploadingImage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Uploading image...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageUploadOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Book View Dialog - View All Images */}
      <Dialog 
        open={bookViewOpen} 
        onClose={() => setBookViewOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Collections sx={{ color: '#1976d2' }} />
            <Box>
              <Typography variant="h6">
                {bookViewCollection?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Collection Images - {bookViewCollection?.image_count} views available
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Front View */}
              {bookViewCollection?.has_front_image && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      border: '2px solid #4caf50',
                      borderRadius: 2,
                      '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                      Front View
                    </Typography>
                    <Box 
                      sx={{ 
                        height: 200, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => {
                        if (bookViewCollection?.front_image_url) {
                          setPreviewImage(bookViewCollection.front_image_url);
                          setPreviewImageAlt(`${bookViewCollection.name} - Front View`);
                          setImagePreviewOpen(true);
                          setImageZoom(1);
                          setImagePosition({ x: 0, y: 0 });
                        }
                      }}
                    >
                      <img
                        src={bookViewCollection?.front_image_url}
                        alt="Front View"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Chip 
                      label="Front View" 
                      color="success" 
                      size="small" 
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
              )}

              {/* Back View */}
              {bookViewCollection?.has_back_image && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      border: '2px solid #2196f3',
                      borderRadius: 2,
                      '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                      Back View
                    </Typography>
                    <Box 
                      sx={{ 
                        height: 200, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => {
                        if (bookViewCollection?.back_image_url) {
                          setPreviewImage(bookViewCollection.back_image_url);
                          setPreviewImageAlt(`${bookViewCollection.name} - Back View`);
                          setImagePreviewOpen(true);
                          setImageZoom(1);
                          setImagePosition({ x: 0, y: 0 });
                        }
                      }}
                    >
                      <img
                        src={bookViewCollection?.back_image_url}
                        alt="Back View"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Chip 
                      label="Back View" 
                      color="primary" 
                      size="small" 
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
              )}

              {/* Top View */}
              {bookViewCollection?.has_top_image && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      border: '2px solid #ff9800',
                      borderRadius: 2,
                      '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                      Top View
                    </Typography>
                    <Box 
                      sx={{ 
                        height: 200, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => {
                        if (bookViewCollection?.top_image_url) {
                          setPreviewImage(bookViewCollection.top_image_url);
                          setPreviewImageAlt(`${bookViewCollection.name} - Top View`);
                          setImagePreviewOpen(true);
                          setImageZoom(1);
                          setImagePosition({ x: 0, y: 0 });
                        }
                      }}
                    >
                      <img
                        src={bookViewCollection?.top_image_url}
                        alt="Top View"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Chip 
                      label="Top View" 
                      sx={{ mt: 1, backgroundColor: '#ff9800', color: 'white' }}
                      size="small"
                    />
                  </Paper>
                </Grid>
              )}

              {/* Bottom View */}
              {bookViewCollection?.has_bottom_image && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      border: '2px solid #9c27b0',
                      borderRadius: 2,
                      '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                      Bottom View
                    </Typography>
                    <Box 
                      sx={{ 
                        height: 200, 
                        borderRadius: 1, 
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => {
                        if (bookViewCollection?.bottom_image_url) {
                          setPreviewImage(bookViewCollection.bottom_image_url);
                          setPreviewImageAlt(`${bookViewCollection.name} - Bottom View`);
                          setImagePreviewOpen(true);
                          setImageZoom(1);
                          setImagePosition({ x: 0, y: 0 });
                        }
                      }}
                    >
                      <img
                        src={bookViewCollection?.bottom_image_url}
                        alt="Bottom View"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Chip 
                      label="Bottom View" 
                      sx={{ mt: 1, backgroundColor: '#9c27b0', color: 'white' }}
                      size="small"
                    />
                  </Paper>
                </Grid>
              )}

              {/* Bill Views - Multiple bills support */}
              {bookViewCollection?.has_bill_image && (
                <>
                  {/* Show all bill images */}
                  {bookViewCollection?.bill_images && bookViewCollection.bill_images.length > 0 ? (
                    bookViewCollection.bill_images.map((billImage, index) => (
                      <Grid item xs={12} sm={6} md={4} key={billImage.id}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            border: '2px solid #f44336',
                            borderRadius: 2,
                            '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                          }}
                        >
                          <Typography variant="h6" gutterBottom sx={{ color: '#f44336', fontWeight: 'bold' }}>
                            Bill/Receipt {index + 1}
                          </Typography>
                          <Box 
                            sx={{ 
                              height: 200, 
                              borderRadius: 1, 
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0'
                            }}
                            onClick={() => {
                              handleImageClick(bookViewCollection, 'bill');
                              // Set the current image index to this bill
                              const billImages = bookViewCollection.bill_images || [];
                              const currentIndex = billImages.findIndex(bi => bi.id === billImage.id);
                              if (currentIndex >= 0) {
                                setCurrentImageIndex(currentIndex);
                                setPreviewImage(billImage.url);
                                setPreviewImageAlt(`${bookViewCollection.name} - Bill ${index + 1}${billImage.filename ? ` - ${billImage.filename}` : ''}`);
                              }
                            }}
                          >
                            <img
                              src={billImage.url}
                              alt={`Bill ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>
                          <Chip 
                            label={`Bill ${index + 1}${billImage.filename ? ` - ${billImage.filename}` : ''}`}
                            color="error" 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    ))
                  ) : (
                    /* Legacy single bill image */
                    bookViewCollection?.bill_image_url && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            border: '2px solid #f44336',
                            borderRadius: 2,
                            '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                          }}
                        >
                          <Typography variant="h6" gutterBottom sx={{ color: '#f44336', fontWeight: 'bold' }}>
                            Bill/Receipt
                          </Typography>
                          <Box 
                            sx={{ 
                              height: 200, 
                              borderRadius: 1, 
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0'
                            }}
                            onClick={() => {
                              if (bookViewCollection?.bill_image_url) {
                                setPreviewImage(bookViewCollection.bill_image_url);
                                setPreviewImageAlt(`${bookViewCollection.name} - Bill/Receipt`);
                                setImagePreviewOpen(true);
                                setImageZoom(1);
                                setImagePosition({ x: 0, y: 0 });
                              }
                            }}
                          >
                            <img
                              src={bookViewCollection?.bill_image_url}
                              alt="Bill/Receipt"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>
                          <Chip 
                            label="Bill/Receipt" 
                            color="error" 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    )
                  )}
                </>
              )}

              {bookViewCollection?.in_bank &&
                (bookViewCollection.bank_deposits || []).map((dep) => (
                  <Grid item xs={12} sm={6} md={4} key={dep.id}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        border: '2px solid #00897b',
                        borderRadius: 2,
                        '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' },
                      }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ color: '#00897b', fontWeight: 'bold' }}>
                        Bank deposit
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {dep.deposited_on} — {dep.received_per_gram_rate_display}
                      </Typography>
                      {dep.has_receipt && dep.receipt_url ? (
                        /\.pdf(\?|$)/i.test(dep.receipt_url) ? (
                          <Box sx={{ py: 2 }}>
                            <Button variant="contained" href={dep.receipt_url} target="_blank" rel="noopener noreferrer">
                              Open receipt (PDF)
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 200,
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0',
                            }}
                            onClick={() => {
                              setPreviewImage(dep.receipt_url!);
                              setPreviewImageAlt(`${bookViewCollection!.name} - Bank ${dep.deposited_on}`);
                              setImagePreviewOpen(true);
                              setImageZoom(1);
                              setImagePosition({ x: 0, y: 0 });
                            }}
                          >
                            <img
                              src={dep.receipt_url}
                              alt="Bank receipt"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No receipt attached
                        </Typography>
                      )}
                      <Chip label="Bank deposit" sx={{ mt: 1, backgroundColor: '#00897b', color: 'white' }} size="small" />
                    </Paper>
                  </Grid>
                ))}
            </Grid>

            {/* Summary */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Collection Summary - Weight by Metal
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Material</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bookViewCollection?.material}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Weight</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bookViewCollection?.material === 'Gold' 
                      ? `${bookViewCollection?.weight} grams (${((bookViewCollection?.weight || 0) / 8.0).toFixed(2)} pavan)`
                      : `${bookViewCollection?.weight} grams`
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Category</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bookViewCollection?.category}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Purchase Amount</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bookViewCollection?.purchase_amount_display}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Per Gram Rate</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bookViewCollection?.per_gram_rate_display || 'Not specified'}
                  </Typography>
                </Grid>
                {bookViewCollection?.calculated_per_gram_rate_display && bookViewCollection.calculated_per_gram_rate_display !== 'Not calculable' && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Calculated Rate</Typography>
                    <Typography variant="body1" fontWeight="bold" color="text.secondary">
                      {bookViewCollection.calculated_per_gram_rate_display}
                    </Typography>
                  </Grid>
                )}
                {bookViewCollection?.in_bank && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Bank deposits</Typography>
                    <Typography variant="body1" fontWeight="bold" color="secondary">
                      {(bookViewCollection.bank_deposits || []).length === 0
                        ? 'None yet — add from Upload images'
                        : `${bookViewCollection.bank_deposits_count ?? bookViewCollection.bank_deposits?.length} record(s); latest: ${bookViewCollection.latest_bank_received_display || '—'}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookViewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default CollectionsPage;


